/**
 * Platform admin card: add or remove credits for a user or a school
 * (ADM-R01, CLAUDE.md §3.6). Wires POST /api/credits/admin/adjust through
 * creditsAPI.adminAdjust. The adjustment is recorded as a credit transaction
 * with the note and the signed-in admin as operator.
 *
 * Its own component so typing in the fields doesn't remount the inputs.
 */
import React, { useState } from 'react';
import { TextInput, TouchableOpacity, View } from 'react-native';
import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { AdminSectionCard, ADMIN_COLORS } from '@/components/admin/AdminUI';
import { creditsAPI } from '../../services/api';
import { useTranslation } from '../../hooks/useTranslation';
import { useToast } from '../../context/ToastContext';
import { confirmDialog } from '../../utils/dialogs';
import {
  creditAdjustErrorKey,
  parseCreditAdjustForm,
  type CreditAdjustTarget,
  type CreditAdjustType,
} from '../../utils/creditAdjust';

const INPUT_STYLE = {
  borderWidth: 1,
  borderColor: ADMIN_COLORS.border,
  borderRadius: 10,
  paddingHorizontal: 12,
  paddingVertical: 8,
  color: ADMIN_COLORS.text.primary,
} as const;

function ChoiceChips<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { key: T; label: string }[];
  value: T;
  onChange: (key: T) => void;
}) {
  return (
    <View className="flex-row flex-wrap" style={{ gap: 8 }}>
      {options.map((option) => {
        const active = option.key === value;
        return (
          <TouchableOpacity
            key={option.key}
            onPress={() => onChange(option.key)}
            accessibilityRole="button"
            accessibilityState={{ selected: active }}
            style={{
              borderWidth: 1,
              borderColor: active ? ADMIN_COLORS.primary : ADMIN_COLORS.border,
              backgroundColor: active ? ADMIN_COLORS.primaryFaint : ADMIN_COLORS.background,
              borderRadius: 999,
              paddingHorizontal: 12,
              paddingVertical: 6,
            }}
          >
            <Text className="text-xs font-semibold" style={{ color: ADMIN_COLORS.text.primary }}>
              {option.label}
            </Text>
          </TouchableOpacity>
        );
      })}
    </View>
  );
}

export default function CreditAdjustCard() {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [target, setTarget] = useState<CreditAdjustTarget>('user');
  const [targetId, setTargetId] = useState('');
  const [delta, setDelta] = useState('');
  const [creditType, setCreditType] = useState<CreditAdjustType>('purchased');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const handleAdjust = async () => {
    const parsed = parseCreditAdjustForm({ target, targetId, delta, creditType, note });
    if ('errorKey' in parsed) {
      showToast(t(parsed.errorKey), 'error');
      return;
    }
    const { payload } = parsed;
    const vars = {
      delta: payload.delta > 0 ? `+${payload.delta}` : String(payload.delta),
      creditType: t(`platformAdmin.creditAdjust.type_${payload.credit_type}`),
      target: t(`platformAdmin.creditAdjust.target_${target}`),
      id: payload.user_id ?? payload.school_id,
    };
    const confirmed = await confirmDialog(
      t('platformAdmin.creditAdjust.confirmTitle'),
      t('platformAdmin.creditAdjust.confirmMessage', vars),
      t('platformAdmin.creditAdjust.action'),
      t('common.cancel'),
    );
    if (!confirmed) return;
    setSaving(true);
    try {
      const result = await creditsAPI.adminAdjust(payload);
      showToast(
        t('platformAdmin.creditAdjust.success', {
          ...vars,
          subscription: result?.balance_after_subscription ?? 0,
          purchased: result?.balance_after_purchased ?? 0,
        }),
        'success',
      );
      setDelta('');
      setNote('');
    } catch (error: any) {
      showToast(t(creditAdjustErrorKey(error?.response?.status ?? error?.status)), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminSectionCard
      icon="account-balance-wallet"
      title={t('platformAdmin.creditAdjust.title')}
      subtitle={t('platformAdmin.creditAdjust.description')}
    >
      <ChoiceChips<CreditAdjustTarget>
        options={[
          { key: 'user', label: t('platformAdmin.creditAdjust.target_user') },
          { key: 'school', label: t('platformAdmin.creditAdjust.target_school') },
        ]}
        value={target}
        onChange={setTarget}
      />
      <TextInput
        value={targetId}
        onChangeText={setTargetId}
        placeholder={t(`platformAdmin.creditAdjust.idPlaceholder_${target}`)}
        accessibilityLabel={t(`platformAdmin.creditAdjust.idPlaceholder_${target}`)}
        keyboardType="number-pad"
        testID="credit-adjust-target-id"
        style={INPUT_STYLE}
      />
      <ChoiceChips<CreditAdjustType>
        options={[
          { key: 'purchased', label: t('platformAdmin.creditAdjust.type_purchased') },
          { key: 'subscription', label: t('platformAdmin.creditAdjust.type_subscription') },
        ]}
        value={creditType}
        onChange={setCreditType}
      />
      <TextInput
        value={delta}
        onChangeText={setDelta}
        placeholder={t('platformAdmin.creditAdjust.deltaPlaceholder')}
        accessibilityLabel={t('platformAdmin.creditAdjust.deltaPlaceholder')}
        keyboardType="numbers-and-punctuation"
        testID="credit-adjust-delta"
        style={INPUT_STYLE}
      />
      <TextInput
        value={note}
        onChangeText={setNote}
        placeholder={t('platformAdmin.creditAdjust.notePlaceholder')}
        accessibilityLabel={t('platformAdmin.creditAdjust.notePlaceholder')}
        testID="credit-adjust-note"
        style={INPUT_STYLE}
      />
      <Button onPress={() => void handleAdjust()} disabled={saving || !targetId.trim() || !delta.trim()}>
        <Text className="text-white font-medium">
          {saving ? t('common.loading') : t('platformAdmin.creditAdjust.action')}
        </Text>
      </Button>
    </AdminSectionCard>
  );
}
